// The IRS legal name rule for an association name.
//
// The IRS accepts letters, digits, hyphens, and ampersands in a legal name, and
// nothing else. It also treats corporate endings as a different kind of entity.
// These checks run at intake and again on the server, and they also guard the
// legal name we derive for the EIN worksheet.

const CORPORATE_ENDINGS = [
  "corp",
  "corporation",
  "inc",
  "incorporated",
  "llc",
  "l.l.c",
  "pllc",
  "lc",
  "pa",
  "ltd",
  "lp",
  "llp",
];

/** Characters the IRS accepts in a legal name. */
const ALLOWED = /^[A-Za-z0-9 \-&]+$/;

export const LEGAL_NAME_HELP =
  "Use letters, numbers, hyphens, and the and sign only. No periods, commas, or other marks.";

/** Plain sentences for anything the IRS legal name rule will not accept. */
export function legalNameWarnings(name: string): string[] {
  const out: string[] = [];
  const trimmed = (name || "").trim();
  if (!trimmed) return out;

  if (!ALLOWED.test(trimmed)) {
    out.push(`The IRS accepts a short set of characters in a name. ${LEGAL_NAME_HELP}`);
  }

  const words = trimmed.toLowerCase().replace(/[^a-z0-9 ]/g, " ").split(/\s+/).filter(Boolean);
  const last = words[words.length - 1] ?? "";
  if (CORPORATE_ENDINGS.includes(last)) {
    out.push(
      `A name cannot end with ${last}. That ending belongs to a company, and this is an association.`,
    );
  }

  if (trimmed.length > 70) {
    out.push("Keep the name to seventy characters or fewer so it fits the government forms.");
  }

  return out;
}

/** True when the name passes the IRS legal name rule. */
export function isValidLegalName(name: string): boolean {
  return legalNameWarnings(name).length === 0;
}

/** The legal name we hand to the IRS, cleaned of anything it will not accept. */
export function deriveLegalName(name: string): string {
  return (name || "")
    .replace(/[^A-Za-z0-9 \-&]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
